import React, { lazy, Suspense, useEffect, useState, useRef, useCallback, } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useLocation,
} from "react-router-dom";
import "bootstrap/dist/css/bootstrap.min.css";
import "./App.css";

import Home from "./Components/Home";
import Header from "./Components/Header/Header";
import LazyRender from "./Components/LazyRender";
import AdminLogin from "./Components/admin/AdminLogin";
import ProtectedAdminRoute from "./ProtectedAdminRoute";

// import Shop from "./Components/Shop/Shop";
// import Products from "./Components/Products/Products";
// import CartPage from "./Components/Cart/CartPage";
// import ImmortalSection from "./Components/ImmortalSection/ImmortalSection";
// import ProtectedCheckout from "./ProtectedCheckout"

import { CartProvider } from "./Components/Cart/CartContext";
import { UserProvider } from "./context/UserContext";
import { ModalProvider } from "./ModalContext";
import { useModal } from "./ModalContext";
import { IoIosNotifications } from "react-icons/io";

const ContactUs = lazy(() => import("./Components/Contact/ContactUs"));
const Footer = lazy(() => import("./Components/Home/Footer"));
const AboutUs = lazy(() => import("./Components/AboutUsPage/AboutUs"));
const PageTest = lazy(() => import("./Components/Cart/PageTest"));
const CheckOut = lazy(() => import("./Components/CheckOut/CheckOut"));
const Test = lazy(() => import("./Test/Test"));
const Wikipedia = lazy(() => import("./Test/Wikipedia"));
const Dashboard = lazy(() => import("./Dasboard/Dashboard"));
const TermsConditions = lazy(
  () => import("./Components/Terms/TermsConditionsV2"),
);
const PaymentSuccess = lazy(
  () => import("./Components/CheckOut/PaymentSuccessOrder"),
);
const ErrorCallback = lazy(
  () => import("./Components/CheckOut/ErrorCallback"),
);
const ArtistPage = lazy(() => import("./Components/Shop/ShopByArtist"));
const SearchResults = lazy(() => import("./Components/Shop/SearchResults"));
const Shop = lazy(() => import("./Components/Shop/Shop"));
const Products = lazy(() => import("./Components/Products/Products"));
const CartPage = lazy(() => import("./Components/Cart/CartPage"));
const ImmortalSection = lazy(
  () => import("./Components/ImmortalSection/ImmortalSection"),
);
const ProtectedCheckout = lazy(() => import("./ProtectedCheckout"));


const RouteLoader = () => (
  <div
    className="route-loader"
    aria-hidden="true"
  />
);
const FooterLoader = () => (
  <div
    className="footer-loader"
    aria-hidden="true"
  />
);

const MainRouteReady = ({ children, onReady }) => {
  useEffect(() => {
    let idleId;
    let timerId;

    /*
      The route has now loaded and committed.
      Wait until the browser is idle before allowing the footer to load.
    */
    if ("requestIdleCallback" in window) {
      idleId = window.requestIdleCallback(onReady, {
        timeout: 1500,
      });
    } else {
      timerId = window.setTimeout(onReady, 300);
    }

    return () => {
      if (idleId !== undefined) {
        window.cancelIdleCallback(idleId);
      }

      if (timerId !== undefined) {
        window.clearTimeout(timerId);
      }
    };
  }, [onReady]);

  return children;
};

// Create a wrapper component to use the useLocation hook
function AppContent() {
  const { openModal } = useModal();
  const location = useLocation();

  const isHome = location.pathname === "/";

  const Ip = "http://localhost:5000/";

  const isAdminRoute =
    location.pathname.startsWith("/dashboard") ||
    location.pathname.startsWith("/admin-login");

  const [readyRouteKey, setReadyRouteKey] = useState(null);

  const isMainComponentReady =
    readyRouteKey === location.key;

  const handleMainComponentReady =
    useCallback(() => {
      setReadyRouteKey(location.key);
    }, [location.key]);

  const [hasSeenIntro, setHasSeenIntro] =
    useState(false);


  useEffect(() => {
    const modalShown =
      localStorage.getItem("termsModalShown");

    // Don't show normal website popup in admin area
    if (isAdminRoute) return;

    if (!modalShown) {
      const timer = setTimeout(() => {
        openModal(
          <IoIosNotifications
            size={40}
            color="#993400"
          />,
          "Login or Register",
          "Create an account or login to unlock special benefits, exclusive offers, and discounts!",
          "Stay updated with our newsletter for new arrivals and promotions.",
          "Easily track your order history and manage your favorites."
        );

        localStorage.setItem(
          "termsModalShown",
          "true"
        );
      }, 300000);

      return () =>
        clearTimeout(timer);
    }
  }, [openModal, isAdminRoute]);


  return (
    <>

      {/* HEADER ONLY FOR NORMAL WEBSITE */}
      {!isAdminRoute && <Header />}


      <Suspense fallback={<RouteLoader />}>

        <MainRouteReady
          key={location.key}
          onReady={handleMainComponentReady}
        >

          <Routes>

            {/* ============================= */}
            {/* NORMAL WEBSITE */}
            {/* ============================= */}

            <Route
              path="/"
              element={
                <Home
                  Ip={Ip}
                  hasSeenIntro={hasSeenIntro}
                  setHasSeenIntro={setHasSeenIntro}
                />
              }
            />

            <Route
              path="/shop"
              element={<Shop />}
            />

            <Route
              path="/contact-us"
              element={<ContactUs />}
            />

            <Route
              path="/aboutus"
              element={<AboutUs />}
            />

            <Route
              path="/CheckOut"
              element={
                <ProtectedCheckout>
                  <CheckOut />
                </ProtectedCheckout>
              }
            />

            <Route
              path="/ImmortalSection"
              element={<ImmortalSection />}
            />

            <Route
              path="/products/:id"
              element={<Products />}
            />

            <Route
              path="/cart"
              element={<CartPage />}
            />

            <Route
              path="/TermsConditions"
              element={<TermsConditions />}
            />

            <Route
              path="/payment-success"
              element={<PaymentSuccess />}
            />

            <Route
              path="/errorCallback"
              element={<ErrorCallback />}
            />

            <Route
              path="/artist/:artist"
              element={<ArtistPage />}
            />

            <Route
              path="/search"
              element={<SearchResults />}
            />

            <Route
              path="/admin-login"
              element={<AdminLogin />}
            />

            <Route
              path="/dashboard"
              element={
                <ProtectedAdminRoute>
                  <Dashboard />
                </ProtectedAdminRoute>
              }
            />

          </Routes>

        </MainRouteReady>

      </Suspense>


      {/* FOOTER ONLY FOR NORMAL WEBSITE */}

      {!isAdminRoute &&
        !isHome &&
        isMainComponentReady && (

          <Suspense
            fallback={<FooterLoader />}
          >
            <LazyRender>
              <Footer />
            </LazyRender>
          </Suspense>

        )}

    </>
  );
}
function App() {
  return (
    <Router>
      <UserProvider>
        <ModalProvider>
          <CartProvider>
            <AppContent />
          </CartProvider>
        </ModalProvider>
      </UserProvider>
    </Router>
  );
}

export default App;
